import { FortuneCell } from '../types';
import { update as coreUpdate } from './workbookInternals';

/** The library stores a number TYPED into a General cell as a
 *  string `v` with `ct.t:'n'` (only a native paste or an already-formatted
 *  cell stores a JS number) — so "numeric" can't require `typeof v ===
 *  'number'` or every typed value would show as Count-only. A cell counts as
 *  numeric when its `ct.t` isn't textual AND its value is either already a
 *  number, or a non-blank numeric string on a `'n'`/`'g'`-typed cell. */
export const isNumericCell = (cell: FortuneCell): boolean => {
    const t = cell.ct?.t;
    if (t === 's' || t === 'inlineStr') return false;
    if (typeof cell.v === 'number') return true;
    if (typeof cell.v !== 'string' || (t !== 'n' && t !== 'g')) return false;
    return cell.v.trim() !== '' && Number.isFinite(Number(cell.v));
};

export interface SelectionRangeLike {
    row: number[];
    column: number[];
}

/** A by-coordinate lookup key, shared by every cell map keyed on (r, c). */
export const cellKey = (r: number, c: number): string => `${r}_${c}`;

export interface SelectionStats {
    /** Every unique cell the ranges cover, blank or not — the ≥2-cell
     *  visibility gate reads this instead of a separate pass. */
    totalCells: number;
    count: number;
    numericCount: number;
    sum: number;
    average: number;
    /** The `ct.fa` of the first numeric cell (row-major, first range) — what
     *  Sum/Average are formatted with. */
    fa?: string;
}

/**
 * Average/Count/Sum over every UNIQUE cell covered by `ranges`
 * (overlapping ranges counted once). Count = non-blank cells. Numeric per
 * `isNumericCell`. Sum/Average only include numeric
 * cells; their format is the `ct.fa` of the first numeric cell encountered in
 * row-major order within the first range that contains one.
 */
export const computeSelectionStats = (
    ranges: SelectionRangeLike[],
    getCell: (r: number, c: number) => FortuneCell | null | undefined,
): SelectionStats => {
    const seen = new Set<string>();
    let count = 0;
    let numericCount = 0;
    let sum = 0;
    let fa: string | undefined;

    ranges.forEach(({ row, column }) => {
        for (let r = row[0]; r <= row[1]; r += 1) {
            for (let c = column[0]; c <= column[1]; c += 1) {
                const key = cellKey(r, c);
                if (seen.has(key)) continue;
                seen.add(key);
                const cell = getCell(r, c);
                if (cell == null || cell.v == null) continue;
                count += 1;
                if (isNumericCell(cell)) {
                    numericCount += 1;
                    sum += typeof cell.v === 'number' ? cell.v : Number(cell.v);
                    if (fa === undefined) fa = cell.ct?.fa;
                }
            }
        }
    });

    return { totalCells: seen.size, count, numericCount, sum, average: numericCount ? sum / numericCount : 0, fa };
};

/**
 * Formats a Sum/Average value with `fa` through core's own SSF `update`
 * (returns a plain formatted string) — General (or an unsafe/missing `fa`,
 * or a throw) falls back to a plain decimal, capped at 10 places with
 * trailing zeros trimmed.
 */
export const formatStat = (n: number, fa: string | undefined): string => {
    if (fa && fa !== 'General' && coreUpdate) {
        try {
            const text = coreUpdate(fa, n);
            if (typeof text === 'string' && text) return text;
        } catch {
            // Falls through to the General formatting below.
        }
    }
    return Number(n.toFixed(10)).toString();
};
